import { Box } from '@mui/material';
import { LoadingSkeleton } from '../../../../common/components';
import GerencialesPieTableSection from '../GerencialesPieTableSection';
import GerencialesBarTableSection from '../GerencialesBarTableSection';
import { PIE_ALLOWED_COLORS } from '../../constants/gerencialesConstants';
import { buildPieData } from '../../utils/gerencialesUtils';
import { formatPercentage } from '../../../../utils/formatUtils';

/**
 * Construye filas de tabla y data de grafico por rango etario.
 * @param {Array<Object>} rows - Filas con conteos por rango.
 * @param {string} withLabel - Label para casos con dato asignado.
 * @param {string} withoutLabel - Label para casos sin dato asignado.
 * @returns {{tableRows: Array<Object>, pieData: Array<Object>}} Data normalizada.
 */
const buildAgeSectionData = (rows, withLabel, withoutLabel) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  const tableRows = safeRows.map((row) => ({
    range: row.range,
    with_count: row.with_count ?? 0,
    without_count: row.without_count ?? 0,
    total: row.total ?? 0,
  }));

  const pieData = safeRows.flatMap((row, index) => {
    const baseIndex = index * 2;
    return [
      {
        name: `${withLabel} ${row.range}`,
        value: Number(row.with_count ?? 0),
        fill: PIE_ALLOWED_COLORS[baseIndex % PIE_ALLOWED_COLORS.length],
      },
      {
        name: `${withoutLabel} ${row.range}`,
        value: Number(row.without_count ?? 0),
        fill: PIE_ALLOWED_COLORS[(baseIndex + 1) % PIE_ALLOWED_COLORS.length],
      },
    ];
  });

  return { tableRows, pieData };
};

/**
 * Renderiza el informe de datos personales de clientes.
 * @param {Object} props - Props del componente.
 * @param {boolean} props.loading - Estado de carga.
 * @param {Object|null} props.results - Resultados del reporte.
 * @param {Object|null} props.header - Header con metadata.
 * @returns {JSX.Element|null} Informe de datos personales.
 */
const PersonalDataReport = ({ loading, results, header }) => {
  if (loading) {
    return <LoadingSkeleton />;
  }

  if (!results) {
    return null;
  }

  const ageRangeRows = Array.isArray(results.age_range) ? results.age_range : [];
  const ageGenderRows = Array.isArray(results.age_gender) ? results.age_gender : [];
  const genderRows = Array.isArray(results.gender) ? results.gender : [];
  const zoneRows = Array.isArray(results.zone) ? results.zone : [];

  const ageRangePie = buildPieData(ageRangeRows, 'range', 'count', PIE_ALLOWED_COLORS);
  const genderLabelMap = {
    M: 'Mujer',
    H: 'Hombre',
    'Sin sexo': 'Sin sexo',
  };
  const ageGenderPie = ageGenderRows.flatMap((row, index) => {
    const slices = [
      {
        name: `${genderLabelMap.M} ${row.range}`,
        value: Number(row.women ?? 0),
        fill: PIE_ALLOWED_COLORS[(index * 2) % PIE_ALLOWED_COLORS.length],
      },
      {
        name: `${genderLabelMap.H} ${row.range}`,
        value: Number(row.men ?? 0),
        fill: PIE_ALLOWED_COLORS[(index * 2 + 1) % PIE_ALLOWED_COLORS.length],
      },
    ];

    if (Number(row.unknown ?? 0) > 0) {
      slices.push({
        name: `Sin sexo ${row.range}`,
        value: Number(row.unknown ?? 0),
        fill: PIE_ALLOWED_COLORS[(index * 2 + 2) % PIE_ALLOWED_COLORS.length],
      });
    }

    return slices;
  });
  const genderDisplayRows = genderRows.map((row) => ({
    ...row,
    label: genderLabelMap[row.label] || row.label,
  }));
  const genderPie = buildPieData(genderDisplayRows, 'label', 'count', PIE_ALLOWED_COLORS);
  const totalClients = header?.total_clients ?? 0;
  const zoneChartData = zoneRows.map((row, index) => ({
    name: row.zone,
    value: Number(row.percent ?? 0),
    fill: PIE_ALLOWED_COLORS[index % PIE_ALLOWED_COLORS.length],
  }));
  const zoneTableRows = [
    ...zoneRows,
    { zone: 'Total clientes activos', count: totalClients, percent: 100, is_total: true },
  ];

  const emailData = buildAgeSectionData(results.email_by_age, 'Con email', 'Sin email');
  const phoneData = buildAgeSectionData(results.phone_by_age, 'Con telefono', 'Sin telefono');
  const appData = buildAgeSectionData(results.app_by_age, 'Usa app', 'No usa app');


  return (
    <Box>
      <GerencialesPieTableSection
        title="Clientes por rango de edad"
        columns={[
          { key: 'range', label: 'Rango', align: 'left' },
          { key: 'count', label: 'Cantidad', align: 'center' },
        ]}
        rows={ageRangeRows}
        pieData={ageRangePie}
        totalLabel="Total clientes activos"
        totalCount={totalClients}
        chartId="gerenciales-personal-age-range"
      />
      <GerencialesPieTableSection
        title="Clientes por rango de edad y sexo"
        columns={[
          { key: 'range', label: 'Rango', align: 'left' },
          { key: 'total', label: 'Cantidad', align: 'center' },
          { key: 'women', label: 'Mujer', align: 'center' },
          { key: 'men', label: 'Hombre', align: 'center' },
        ]}
        rows={ageGenderRows}
        pieData={ageGenderPie}
        totalLabel="Total clientes activos"
        totalCount={totalClients}
        chartId="gerenciales-personal-age-gender"
      />
      <GerencialesPieTableSection
        title="Clientes por sexo"
        columns={[
          { key: 'label', label: 'Sexo', align: 'left' },
          { key: 'count', label: 'Cantidad', align: 'center' },
        ]}
        rows={genderDisplayRows}
        pieData={genderPie}
        totalLabel="Total clientes activos"
        totalCount={totalClients}
        chartId="gerenciales-personal-gender"
      />
      <GerencialesBarTableSection
        title="Clientes por zona"
        columns={[
          { key: 'zone', label: 'Descrip. zona', align: 'left' },
          { key: 'count', label: 'Cantidad', align: 'center' },
          { key: 'percent', label: '%', align: 'center' },
        ]}
        rows={zoneTableRows}
        chartData={zoneChartData}
        chartId="gerenciales-personal-zone"
        xAxisLabel="% clientes por zonas"
        valueFormatter={(value) => formatPercentage(value, { decimals: 2, fallback: '0%' })}
      />
      <GerencialesPieTableSection
        title="Clientes con email asignado segun rango etario"
        columns={[
          { key: 'range', label: 'Rango', align: 'left' },
          { key: 'with_count', label: 'Con email', align: 'center' },
          { key: 'without_count', label: 'Sin email', align: 'center' },
          { key: 'total', label: 'Total', align: 'center' },
        ]}
        rows={emailData.tableRows}
        pieData={emailData.pieData}
        totalLabel="Total clientes activos"
        totalCount={totalClients}
        chartId="gerenciales-personal-email"
      />
      <GerencialesPieTableSection
        title="Clientes con telefono asignado segun rango etario"
        columns={[
          { key: 'range', label: 'Rango', align: 'left' },
          { key: 'with_count', label: 'Con telefono', align: 'center' },
          { key: 'without_count', label: 'Sin telefono', align: 'center' },
          { key: 'total', label: 'Total', align: 'center' },
        ]}
        rows={phoneData.tableRows}
        pieData={phoneData.pieData}
        totalLabel="Total clientes activos"
        totalCount={totalClients}
        chartId="gerenciales-personal-phone"
      />
      <GerencialesPieTableSection
        title="Clientes que usan la app mobile segun rango etario"
        columns={[
          { key: 'range', label: 'Rango', align: 'left' },
          { key: 'with_count', label: 'Usa app', align: 'center' },
          { key: 'without_count', label: 'No usa app', align: 'center' },
          { key: 'total', label: 'Total', align: 'center' },
        ]}
        rows={appData.tableRows}
        pieData={appData.pieData}
        totalLabel="Total clientes activos"
        totalCount={totalClients}
        chartId="gerenciales-personal-app"
      />
    </Box>
  );
};

export default PersonalDataReport;
